import {
  CHIP_FIELDS,
  CHIP_FIELD_LABELS,
  DEFAULT_IMPORT_DECISION,
  EMPTY_MAPPING,
  FILLABLE_FIELDS,
  FILLABLE_FIELD_LABELS,
  IMPORT_ROW_STATUS_LABELS,
  MAX_IMPORT_ROWS,
  analyzeImport,
  applyUniformFill,
  autoMapHeaders,
  cellToString,
  lacksAthleteData,
  planImport,
  rowToPayload,
  type ChipComparable,
  type ChipField,
  type ColumnMapping,
  type ExistingChipMap,
  type FillableField,
  type ImportPlan,
  type ImportResultDto,
  type ImportRow,
  type ImportRowDecision,
} from '@pietrochip/shared';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from '../api/endpoints';
import { describeError } from '../api/client';
import { Modal, Notice } from './ui';

/**
 * Importazione dei chip da file o da testo incollato.
 *
 * Il file non viene mai inviato al server: viene letto qui, nel browser, e al
 * server arriva solo l'elenco già pulito. Così l'anteprima è immediata — si
 * vedono subito duplicati e righe senza codice — e il server non deve
 * interpretare formati altrui.
 *
 * Tre passaggi, ma il primo e il terzo sono quelli che l'operatore guarda:
 * scegliere il file, e **confermare guardando cosa entrerà davvero**. La
 * mappatura delle colonne compare solo quando i nomi non si riconoscono da soli.
 */

type Step = 'source' | 'preview';

type ParsedFile = {
  fileName: string;
  /** Tutte le righe grezze, intestazione compresa. */
  rows: unknown[][];
};

/**
 * Come riempire i campi atleta di un file che porta solo i codici.
 *
 * `blank` non è un'omissione: è la scelta di completarli dopo a mano, e va
 * lasciata possibile perché spesso i dati non ci sono ancora.
 */
type FillMode = 'blank' | 'uniform';

export function ImportWizard({
  eventId,
  onClose,
  onImported,
}: {
  eventId: string;
  onClose: () => void;
  onImported: (result: ImportResultDto) => void;
}) {
  const [step, setStep] = useState<Step>('source');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [existingChips, setExistingChips] = useState<ExistingChipMap>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);

  /** Cosa fare delle righe in conflitto, per `rowIndex`. */
  const [decisions, setDecisions] = useState<ReadonlyMap<number, ImportRowDecision>>(new Map());
  const [fillMode, setFillMode] = useState<FillMode>('blank');
  const [fill, setFill] = useState<Partial<Record<FillableField, string>>>({});

  // I dati già in elenco servono a due cose: segnalare «già nell'evento» e
  // **distinguere** una riga identica a quella esistente da una che porta dati
  // diversi. Solo la seconda merita una decisione. Se la chiamata fallisce si
  // importa lo stesso: il server deduplica comunque.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { chips } = await api.fetchEventCodes(eventId);
        if (cancelled) return;
        setExistingChips(
          new Map(chips.map((chip) => [chip.code, toComparable(chip)])),
        );
      } catch {
        /* anteprima meno precisa, importazione comunque corretta */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const headers = useMemo(() => {
    if (!parsed) return [];
    if (!hasHeader) return [];
    return (parsed.rows[0] ?? []).map((cell) => cellToString(cell) ?? '');
  }, [parsed, hasHeader]);

  const dataRows = useMemo(() => {
    if (!parsed) return [];
    return hasHeader ? parsed.rows.slice(1) : parsed.rows;
  }, [parsed, hasHeader]);

  const columnCount = useMemo(() => {
    if (!parsed) return 0;
    return parsed.rows.reduce((max, row) => Math.max(max, row.length), 0);
  }, [parsed]);

  /**
   * L'analisi del file così com'è, prima del riempimento in blocco.
   *
   * Serve a due cose che il riempimento cambierebbe: decidere **se** chiedere
   * come riempire (un file di soli codici) e mostrare i valori veri del file.
   */
  const rawAnalysis = useMemo(() => {
    if (!mapping) return null;
    return analyzeImport(dataRows, mapping, existingChips);
  }, [dataRows, mapping, existingChips]);

  /** Il file di soli codici: nessun dato atleta da nessuna parte. */
  const needsFill = rawAnalysis !== null && lacksAthleteData(rawAnalysis);

  /**
   * Quello che entrerà davvero: l'analisi con i campi vuoti già riempiti.
   *
   * L'anteprima legge da qui e non dal file, perché il riquadro deve mostrare il
   * risultato e non la materia prima — chi ha appena scritto «Squadra: ASD
   * Esempio» deve vederla comparire su tutte le righe prima di confermare.
   */
  const analysis = useMemo(() => {
    if (!rawAnalysis) return null;
    return applyUniformFill(rawAnalysis, fillMode === 'uniform' ? fill : {});
  }, [rawAnalysis, fillMode, fill]);

  /** La divisione fra quello che entra e quello che resta com'era. */
  const plan: ImportPlan = useMemo(
    () => (analysis ? planImport(analysis, decisions) : { inserts: [], updates: [], kept: [] }),
    [analysis, decisions],
  );

  /**
   * Le decisioni valgono per `rowIndex`, cioè per la posizione nel file: quando
   * cambia la mappatura o la riga di intestazione, la riga 7 non è più quella di
   * prima e le scelte fatte non hanno più senso. Si azzerano invece di essere
   * applicate alla cieca.
   */
  useEffect(() => {
    setDecisions(new Map());
  }, [rawAnalysis]);

  const conflicts = useMemo(
    () => (analysis?.rows ?? []).filter((row) => row.status === 'conflict'),
    [analysis],
  );

  function setDecision(rowIndex: number, decision: ImportRowDecision) {
    setDecisions((current) => new Map(current).set(rowIndex, decision));
  }

  /** La scelta in blocco: con un file di migliaia di conflitti è l'unica via. */
  function setAllDecisions(decision: ImportRowDecision) {
    setDecisions(new Map(conflicts.map((row) => [row.rowIndex, decision])));
  }

  function applyParsed(next: ParsedFile) {
    setParsed(next);
    setHasHeader(true);
    const detectedHeaders = (next.rows[0] ?? []).map((cell) => cellToString(cell) ?? '');
    setMapping(autoMapHeaders(detectedHeaders));
    setDecisions(new Map());
    setStep('preview');
    setError(null);
  }

  /**
   * Attiva/disattiva la prima riga come intestazione.
   *
   * Serve ai file che arrivano senza intestazione: in quel caso la riga zero è
   * un atleta, e trattarla come nomi di colonna perderebbe un chip e
   * etichetterebbe le colonne con dati veri. Senza intestazione la mappatura si
   * rifà da zero a mano, perché non c'è niente da riconoscere.
   */
  function toggleHeader(value: boolean) {
    if (!parsed) return;
    setHasHeader(value);
    const nextHeaders = value
      ? (parsed.rows[0] ?? []).map((cell) => cellToString(cell) ?? '')
      : [];
    // Senza intestazione non c'è niente da riconoscere, ma la mappatura resta
    // vuota e modificabile: azzerarla a `null` lascerebbe l'operatore senza
    // menu da compilare.
    setMapping(value ? autoMapHeaders(nextHeaders) : { ...EMPTY_MAPPING });
  }

  async function readFile(file: File) {
    setReading(true);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const rows = await parseWorkbook(buffer);
      if (rows.length === 0) {
        setError('Il file non contiene righe. Controlla che il foglio giusto sia il primo.');
        return;
      }
      applyParsed({ fileName: file.name, rows });
    } catch (cause) {
      setError(
        `Non riesco a leggere il file: ${describeError(cause)} Prova a salvarlo di nuovo in formato .xlsx o .csv.`,
      );
    } finally {
      setReading(false);
    }
  }

  async function readText(text: string) {
    setReading(true);
    setError(null);
    try {
      const rows = await parseDelimitedText(text);
      if (rows.length === 0) {
        setError('Non c’è nessuna riga da importare nel testo incollato.');
        return;
      }
      applyParsed({ fileName: 'testo incollato', rows });
    } catch (cause) {
      setError(`Non riesco a leggere il testo incollato: ${describeError(cause)}`);
    } finally {
      setReading(false);
    }
  }

  /**
   * Manda al server il piano: i chip nuovi e gli aggiornamenti decisi.
   *
   * Le due liste viaggiano insieme perché l'operatore ha confermato **una**
   * anteprima: se le righe da aggiornare arrivassero in una chiamata separata e
   * questa fallisse, l'elenco resterebbe a metà di quello che si è visto sullo
   * schermo.
   */
  async function confirm() {
    if (!analysis || !mapping || busy) return;

    const chips = plan.inserts.map((row) => ({ code: row.code, ...rowToPayload(row) }));
    const updates = plan.updates.map((row) => ({ code: row.code, ...rowToPayload(row) }));

    if (chips.length === 0 && updates.length === 0) {
      setError('Nessuna riga da importare: sono tutte già presenti o senza codice.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await api.importChips(eventId, { chips, updates });
      onImported(result);
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  }

  const codeMapped = mapping?.chipCode !== null && mapping?.chipCode !== undefined;
  const tooManyRows = plan.inserts.length > MAX_IMPORT_ROWS;
  const toWrite = plan.inserts.length + plan.updates.length;

  const fillPanel = needsFill ? (
    <FillPanel mode={fillMode} onMode={setFillMode} values={fill} onChange={setFill} />
  ) : null;

  return (
    <Modal
      wide
      title="Importa i chip"
      onClose={onClose}
      footer={
        step === 'source' ? (
          <button type="button" className="btn" onClick={onClose}>
            Annulla
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setStep('source');
                setParsed(null);
                setMapping(null);
                setDecisions(new Map());
                setError(null);
              }}
            >
              Cambia file
            </button>
            <span className="shell__spacer" />
            <button
              type="button"
              className="btn btn--primary btn--lg"
              disabled={busy || !codeMapped || tooManyRows || toWrite === 0}
              onClick={() => void confirm()}
            >
              {busy ? 'Importazione…' : confirmLabel(plan)}
            </button>
          </>
        )
      }
    >
      {step === 'source' ? (
        <SourceStep onFile={(file) => void readFile(file)} onText={(text) => void readText(text)} reading={reading} />
      ) : (
        <PreviewStep
          parsed={parsed}
          headers={headers}
          columnCount={columnCount}
          hasHeader={hasHeader}
          onToggleHeader={toggleHeader}
          mapping={mapping}
          onMappingChange={setMapping}
          analysis={analysis}
          decisions={decisions}
          onDecision={setDecision}
          onAllDecisions={setAllDecisions}
          fillPanel={fillPanel}
          tooManyRows={tooManyRows}
        />
      )}

      {error && <Notice tone="danger">{error}</Notice>}
    </Modal>
  );
}

/**
 * L'etichetta del pulsante di conferma, che dice esattamente cosa succederà.
 *
 * «Importa 12 chip» quando quattro di quelli sono aggiornamenti racconta una
 * cosa falsa: chi ha scelto «aggiorna» su un conflitto deve ritrovare la parola
 * prima di premere, non solo nel messaggio finale.
 */
function confirmLabel(plan: ImportPlan): string {
  const inserts = plan.inserts.length;
  const updates = plan.updates.length;

  if (inserts === 0) return `Aggiorna ${updates} chip`;
  if (updates === 0) return `Importa ${inserts} chip`;
  return `Importa ${inserts} e aggiorna ${updates}`;
}

/** L'anagrafica di un chip esistente, ridotta ai campi che un file può portare. */
function toComparable(chip: {
  team: string | null;
  firstName: string | null;
  lastName: string | null;
  category: string | null;
  raceNumber: string | null;
  uciId: string | null;
  race: string | null;
}): ChipComparable {
  return {
    team: chip.team,
    firstName: chip.firstName,
    lastName: chip.lastName,
    category: chip.category,
    raceNumber: chip.raceNumber,
    uciId: chip.uciId,
    race: chip.race,
  };
}

// ── Riempimento dei campi atleta ─────────────────────────────────────────────

/**
 * La domanda che si fa a un file di soli codici.
 *
 * Un elenco di transponder nudi è un caso normale — è quello che si ha prima
 * che le società mandino le iscrizioni — e importarlo così com'è lascia duecento
 * righe senza nome che poi nessuno ricollega. Ma riempirle con un valore
 * inventato è peggio: l'applicazione non può sapere se è una gara sola, quindi
 * chiede. Entrambe le strade restano aperte, e nessuna è quella «giusta».
 */
function FillPanel({
  mode,
  onMode,
  values,
  onChange,
}: {
  mode: FillMode;
  onMode: (mode: FillMode) => void;
  values: Partial<Record<FillableField, string>>;
  onChange: (values: Partial<Record<FillableField, string>>) => void;
}) {
  return (
    <div className="card">
      <div className="card__body stack">
        <h3 className="card__title">Il file ha solo i codici</h3>
        <p className="muted" style={{ fontSize: '0.875rem' }}>
          Nessuna riga porta squadra, nome, cognome o gara. Puoi importarli così — restano vuoti
          e li completi dopo — oppure dare un valore uguale a tutte le righe, se vale per tutte.
        </p>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`btn btn--sm ${mode === 'blank' ? 'btn--primary' : ''}`}
            aria-pressed={mode === 'blank'}
            onClick={() => onMode('blank')}
          >
            Lascia vuoti
          </button>
          <button
            type="button"
            className={`btn btn--sm ${mode === 'uniform' ? 'btn--primary' : ''}`}
            aria-pressed={mode === 'uniform'}
            onClick={() => onMode('uniform')}
          >
            Stesso valore per tutte le righe
          </button>
        </div>

        {mode === 'uniform' && (
          <>
            <div className="mapping">
              {FILLABLE_FIELDS.map((field) => (
                <Fragment key={field}>
                  <label className="mapping__field" htmlFor={`fill-${field}`}>
                    {FILLABLE_FIELD_LABELS[field]}
                  </label>
                  <input
                    id={`fill-${field}`}
                    className="input"
                    value={values[field] ?? ''}
                    onChange={(event) =>
                      onChange({ ...values, [field]: event.target.value })
                    }
                    placeholder="—"
                  />
                  <span />
                </Fragment>
              ))}
            </div>
            <p className="faint" style={{ fontSize: '0.8125rem' }}>
              I campi lasciati vuoti restano tali, e un valore già scritto nel file non viene mai
              sovrascritto da quello scritto qui.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Passaggio 1: da dove arrivano i dati ─────────────────────────────────────

function SourceStep({
  onFile,
  onText,
  reading,
}: {
  onFile: (file: File) => void;
  onText: (text: string) => void;
  reading: boolean;
}) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="stack">
      <Notice>
        Colonne riconosciute: <code className="mono">chip_code</code> (obbligatoria),
        <code className="mono"> team_name</code>, <code className="mono">first_name</code>,
        <code className="mono"> last_name</code>, <code className="mono">category</code>,
        <code className="mono"> uci_id</code>, <code className="mono">race_number</code>,
        <code className="mono"> race</code>. Spazi, maiuscole e simboli nelle intestazioni non
        contano, e una colonna si può sempre assegnare a mano.
      </Notice>

      <div className="card">
        <div className="card__body stack">
          <h3 className="card__title">Da file</h3>
          <p className="muted" style={{ fontSize: '0.875rem' }}>
            Excel (.xlsx, .xls) o CSV. Viene letto il primo foglio. Il file resta sul tuo
            dispositivo: al server arriva solo l’elenco dei chip.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Si azzera subito: senza questo, riscegliere lo stesso file dopo
              // averlo corretto non farebbe partire nulla.
              event.target.value = '';
              if (file) onFile(file);
            }}
          />
          <button
            type="button"
            className="btn btn--primary btn--lg"
            disabled={reading}
            onClick={() => inputRef.current?.click()}
          >
            {reading ? 'Lettura…' : 'Scegli il file'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card__body stack">
          <h3 className="card__title">Oppure incolla il testo</h3>
          <p className="muted" style={{ fontSize: '0.875rem' }}>
            Una riga per chip, con l’intestazione nella prima riga. Vanno bene sia le tabulazioni
            copiate da Excel sia le virgole di un CSV.
          </p>
          <textarea
            className="textarea"
            rows={6}
            value={text}
            onChange={(event) => setText(event.target.value)}
            spellCheck={false}
            placeholder={'chip_code,last_name,first_name,team_name\nAB1234,Rossi,Marco,ASD Esempio'}
          />
          <button
            type="button"
            className="btn btn--lg"
            disabled={reading || text.trim() === ''}
            onClick={() => onText(text)}
          >
            Analizza il testo
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Passaggio 2: cosa entrerà davvero ────────────────────────────────────────

function PreviewStep({
  parsed,
  headers,
  columnCount,
  hasHeader,
  onToggleHeader,
  mapping,
  onMappingChange,
  analysis,
  decisions,
  onDecision,
  onAllDecisions,
  fillPanel,
  tooManyRows,
}: {
  parsed: ParsedFile | null;
  headers: string[];
  columnCount: number;
  hasHeader: boolean;
  onToggleHeader: (value: boolean) => void;
  mapping: ColumnMapping | null;
  onMappingChange: (mapping: ColumnMapping) => void;
  analysis: ReturnType<typeof analyzeImport> | null;
  decisions: ReadonlyMap<number, ImportRowDecision>;
  onDecision: (rowIndex: number, decision: ImportRowDecision) => void;
  onAllDecisions: (decision: ImportRowDecision) => void;
  /** Il riquadro «il file ha solo i codici», quando serve. */
  fillPanel: ReactNode;
  tooManyRows: boolean;
}) {
  const [showOnlyProblems, setShowOnlyProblems] = useState(false);
  const [manualMapping, setManualMapping] = useState(false);

  const autoDetected = useMemo(
    () => (hasHeader ? autoMapHeaders(headers) : null),
    [hasHeader, headers],
  );

  // La mappatura si mostra da sola solo se serve: se il riconoscimento
  // automatico non trova il codice chip, l'operatore deve intervenire, ed è
  // meglio che se ne accorga subito invece di cercare il pulsante.
  const needsMapping = mapping === null || mapping.chipCode === null || manualMapping;

  const stats = analysis?.stats;
  const rows = analysis?.rows ?? [];
  const conflicts = useMemo(() => rows.filter((row) => row.status === 'conflict'), [rows]);
  const decidedToUpdate = conflicts.filter(
    (row) => decisions.get(row.rowIndex) === 'update',
  ).length;

  const visibleRows = useMemo(() => {
    const filtered = showOnlyProblems ? rows.filter((row) => row.status !== 'ok') : rows;
    return filtered.slice(0, 200);
  }, [rows, showOnlyProblems]);

  function setColumn(field: ChipField, value: string) {
    if (!mapping) return;
    const index = value === '' ? null : Number(value);
    onMappingChange({ ...mapping, [field]: index });
  }

  return (
    <div className="stack">
      <div className="row">
        <strong>{parsed?.fileName}</strong>
        <span className="shell__spacer" />
        <label className="row" style={{ gap: 6, fontSize: '0.875rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hasHeader}
            onChange={(event) => onToggleHeader(event.target.checked)}
          />
          La prima riga contiene le intestazioni
        </label>
      </div>

      {stats && (
        <div className="stat-grid">
          <div className="stat">
            <div className="label">Righe lette</div>
            <div className="stat__value">{stats.total}</div>
          </div>
          <div className="stat stat--ok">
            <div className="label">Nuove</div>
            <div className="stat__value">{stats.importable}</div>
          </div>
          <div className="stat stat--warn">
            <div className="label">Da decidere</div>
            <div className="stat__value">{stats.conflicts}</div>
          </div>
          <div className="stat stat--warn">
            <div className="label">Già nell’evento</div>
            <div className="stat__value">{stats.duplicateInEvent}</div>
          </div>
          <div className="stat stat--warn">
            <div className="label">Doppioni nel file</div>
            <div className="stat__value">{stats.duplicateInFile}</div>
          </div>
          <div className="stat stat--warn">
            <div className="label">Senza codice</div>
            <div className="stat__value">{stats.missingCode}</div>
          </div>
        </div>
      )}

      {fillPanel}

      {tooManyRows && (
        <Notice tone="danger">
          Troppe righe da importare in una volta (massimo {MAX_IMPORT_ROWS.toLocaleString('it-IT')}).
          Dividi il file in due parti.
        </Notice>
      )}

      {conflicts.length > 0 && !needsMapping && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.875rem' }}>
            <strong>{conflicts.length}</strong>{' '}
            {conflicts.length === 1 ? 'codice è già in elenco' : 'codici sono già in elenco'} con
            dati diversi dal file
            {decidedToUpdate > 0 && <> · {decidedToUpdate} da aggiornare</>}.
          </span>
          <span className="shell__spacer" />
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={() => onAllDecisions('keep')}
          >
            Tieni tutto com’è
          </button>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => onAllDecisions('update')}
          >
            Aggiorna tutto dal file
          </button>
        </div>
      )}

      {stats && stats.importable < stats.total && (
        <Notice tone="missing">
          Le righe segnalate non verranno importate due volte, e le restituzioni già registrate
          restano. Puoi importare un file corretto più tardi, sullo stesso evento, senza perdere
          niente.
        </Notice>
      )}

      {needsMapping && mapping && (
        <div className="stack">
          <h3 className="card__title">Abbina le colonne</h3>
          <p className="muted" style={{ fontSize: '0.875rem' }}>
            Le intestazioni del file non bastano a riconoscere i campi. Indica tu quale colonna
            contiene cosa: solo il <strong>codice chip</strong> è obbligatorio.
          </p>
          <div className="mapping">
            {CHIP_FIELDS.map((field) => (
              <ColumnPicker
                key={field}
                field={field}
                headers={headers}
                columnCount={columnCount}
                value={mapping[field]}
                onChange={(value) => setColumn(field, value)}
                misconfigured={
                  (autoDetected?.[field] ?? null) !== mapping[field] && mapping[field] === null
                }
              />
            ))}
          </div>
          <div className="row">
            <button
              type="button"
              className="btn btn--sm btn--quiet"
              onClick={() => {
                onMappingChange(autoMapHeaders(headers));
                setManualMapping(false);
              }}
            >
              Riprova il riconoscimento automatico
            </button>
          </div>
        </div>
      )}

      {!needsMapping && (
        <button type="button" className="btn btn--sm btn--quiet" onClick={() => setManualMapping(true)}>
          Modifica l’abbinamento delle colonne
        </button>
      )}

      {analysis && (
        <>
          <div className="row">
            <h3 className="card__title">Anteprima</h3>
            <span className="shell__spacer" />
            <label className="row" style={{ gap: 6, fontSize: '0.875rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showOnlyProblems}
                onChange={(event) => setShowOnlyProblems(event.target.checked)}
              />
              Solo le righe da controllare
            </label>
          </div>

          {rows.length === 0 ? (
            <Notice>Il file non contiene righe oltre all’intestazione.</Notice>
          ) : (
            <>
              <div className="preview-scroll">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Riga</th>
                      <th>Esito</th>
                      <th>Codice</th>
                      <th>Cognome</th>
                      <th>Nome</th>
                      <th>Squadra</th>
                      <th>Gara</th>
                      <th>N. gara</th>
                      <th>Decisione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.rowIndex}>
                        <td className="mono faint">{row.rowIndex + (hasHeader ? 2 : 1)}</td>
                        <td>
                          <span className={`state ${row.status === 'ok' ? 'state--returned' : 'state--missing'}`}>
                            <span className="state__mark" aria-hidden="true" />
                            {IMPORT_ROW_STATUS_LABELS[row.status]}
                          </span>
                        </td>
                        <td className="mono">{row.code || <span className="faint">—</span>}</td>
                        <td>
                          <PreviewCell row={row} field="lastName" />
                        </td>
                        <td>
                          <PreviewCell row={row} field="firstName" />
                        </td>
                        <td>
                          <PreviewCell row={row} field="team" />
                        </td>
                        <td>
                          <PreviewCell row={row} field="race" />
                        </td>
                        <td className="mono">
                          <PreviewCell row={row} field="raceNumber" />
                        </td>
                        <td>
                          {row.status === 'conflict' ? (
                            <DecisionPicker
                              decision={decisions.get(row.rowIndex) ?? DEFAULT_IMPORT_DECISION}
                              code={row.code}
                              onChange={(decision) => onDecision(row.rowIndex, decision)}
                            />
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {visibleRows.length < rows.length && (
                <p className="faint" style={{ fontSize: '0.8125rem' }}>
                  Mostrate {visibleRows.length} righe su {rows.length}. L’importazione riguarda
                  tutte.
                </p>
              )}
            </>
          )}
        </>
      )}

      {analysis && !needsMapping && analysis.stats.importable === 0 && (
        <Notice tone="missing">
          Nessuna riga da importare. Se il file è già stato importato, è tutto a posto: i chip
          sono già nell’evento.
        </Notice>
      )}
    </div>
  );
}

/**
 * Il valore che il file porta per un campo, con accanto quello che c'è oggi.
 *
 * Solo per le righe in conflitto, e solo per i campi che divergono davvero: è
 * l'unico punto in cui l'operatore può vedere **cosa** sta per sostituire
 * qualcos'altro. Un «Aggiorna» scelto senza vedere il prima e il dopo è una
 * scommessa sui dati di un atleta.
 */
function PreviewCell({ row, field }: { row: ImportRow; field: FillableField }) {
  const value = row[field];
  const differs = row.status === 'conflict' && row.differingFields.includes(field);
  const previous = differs ? (row.existing?.[field] ?? null) : null;

  return (
    <>
      {value ?? <span className="faint">—</span>}
      {differs && (
        <span className="faint" style={{ fontSize: '0.8125rem' }}>
          {' '}
          oggi {previous ?? 'vuoto'}
        </span>
      )}
    </>
  );
}

/**
 * Tieni o aggiorna, per una riga in conflitto.
 *
 * Due pulsanti e non un menu: la scelta è binaria, si fa una volta per riga
 * mentre si scorre l'anteprima, e un menu a tendina costerebbe due tocchi per
 * leggere quali sono le opzioni.
 */
function DecisionPicker({
  decision,
  code,
  onChange,
}: {
  decision: ImportRowDecision;
  code: string;
  onChange: (decision: ImportRowDecision) => void;
}) {
  return (
    <span className="row" style={{ gap: 4 }}>
      <button
        type="button"
        className={`btn btn--sm ${decision === 'keep' ? '' : 'btn--quiet'}`}
        aria-pressed={decision === 'keep'}
        onClick={() => onChange('keep')}
        aria-label={`Tieni i dati già in elenco per ${code}`}
      >
        Tieni
      </button>
      <button
        type="button"
        className={`btn btn--sm ${decision === 'update' ? '' : 'btn--quiet'}`}
        aria-pressed={decision === 'update'}
        onClick={() => onChange('update')}
        aria-label={`Aggiorna ${code} con i dati del file`}
      >
        Aggiorna
      </button>
    </span>
  );
}

function ColumnPicker({
  field,
  headers,
  columnCount,
  value,
  onChange,
  misconfigured,
}: {
  field: ChipField;
  headers: string[];
  columnCount: number;
  value: number | null;
  onChange: (value: string) => void;
  misconfigured: boolean;
}) {
  const required = field === 'chipCode';
  const columns = Array.from({ length: columnCount }, (_, index) => index);

  return (
    <>
      <label className={`mapping__field ${misconfigured ? 'mapping__required' : ''}`} htmlFor={`map-${field}`}>
        {CHIP_FIELD_LABELS[field]}
        {required && <span className="mapping__required"> *</span>}
      </label>

      <select
        id={`map-${field}`}
        className="select"
        value={value === null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={required && value === null}
      >
        <option value="">— non importare —</option>
        {columns.map((index) => (
          <option key={index} value={index}>
            {columnLabel(headers[index], index)}
          </option>
        ))}
      </select>

      <span className="faint truncate" style={{ fontSize: '0.8125rem' }}>
        {value === null ? '' : columnLabel(headers[value], value)}
      </span>
    </>
  );
}

function columnLabel(header: string | undefined, index: number): string {
  const trimmed = header?.trim();
  return trimmed ? `${trimmed} (${index + 1})` : `Colonna ${index + 1}`;
}

// ── Lettura dei file ─────────────────────────────────────────────────────────

/**
 * La libreria dei fogli di calcolo si carica solo qui, al primo import.
 *
 * Pesa più di tutto il resto dell'applicazione messa insieme, e serve in un
 * momento solo — l'importazione, davanti a una scrivania. Tenerla fuori dal
 * pacchetto iniziale è la differenza fra un'app che si apre subito sul tablet e
 * una che ci mette qualche secondo in più ogni volta.
 */
async function loadXlsx() {
  return import('xlsx');
}

async function parseWorkbook(buffer: ArrayBuffer): Promise<unknown[][]> {
  const XLSX = await loadXlsx();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];

  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });
}

async function parseDelimitedText(text: string): Promise<unknown[][]> {
  const XLSX = await loadXlsx();
  const workbook = XLSX.read(text, { type: 'string', raw: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];

  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });
}
